import { OrderReceipt } from '@/components/customer/OrderReceipt';
import { Button } from '@/components/ui/button';
import type { OrderResponse } from '@/queries/useCart';
import { CheckCircle2, Download, Printer, Share2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface OrderConfirmationScreenProps {
  order: OrderResponse['data'];
  eta: number;
  onStartNewOrder: () => void;
  restaurantName?: string;
  branchName?: string;
  branchAddress?: string;
  branchPhone?: string;
  tableNumber?: string | null;
}

export function OrderConfirmationScreen({
  order,
  eta,
  onStartNewOrder,
  restaurantName,
  branchName,
  branchAddress,
  branchPhone,
  tableNumber,
}: OrderConfirmationScreenProps) {
  const { i18n } = useTranslation('customer');
  const isRTL = i18n.language === 'ar';
  const [showReceipt, setShowReceipt] = useState(false);

  const shortOrderId = order.orderId.substring(0, 8).toUpperCase();

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    // TODO: Implement PDF download using html2pdf or similar
    // For now, just trigger print dialog
    handlePrint();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: isRTL ? 'إيصال الطلب' : 'Order Receipt',
          text: isRTL
            ? `طلب #${shortOrderId} - ${(order.total / 100).toFixed(1)} EGP`
            : `Order #${shortOrderId} - ${(order.total / 100).toFixed(1)} EGP`,
          url: window.location.href,
        });
      } catch (error) {
        // User cancelled or error occurred
        console.log('Share cancelled or failed');
      }
    } else {
      // Fallback: Copy to clipboard
      const text = `${isRTL ? 'طلب' : 'Order'} #${shortOrderId} - ${(order.total / 100).toFixed(1)} EGP`;
      await navigator.clipboard.writeText(text);
      // TODO: Show toast notification
    }
  };

  // Show receipt view
  if (showReceipt) {
    return (
      <div className="w-full max-w-2xl mx-auto px-4 py-8" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => setShowReceipt(false)}
          className="mb-4"
        >
          {isRTL ? '← العودة' : '← Back'}
        </Button>

        {/* Receipt */}
        <OrderReceipt
          order={order}
          restaurantName={restaurantName}
          branchName={branchName}
          branchAddress={branchAddress}
          branchPhone={branchPhone}
          tableNumber={tableNumber}
        />

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6 print:hidden">
          <Button
            variant="outline"
            onClick={handlePrint}
            className="flex-1"
          >
            <Printer className="w-4 h-4 mr-2" />
            {isRTL ? 'اطبع' : 'Print'}
          </Button>
          <Button
            variant="outline"
            onClick={handleDownload}
            className="flex-1"
          >
            <Download className="w-4 h-4 mr-2" />
            {isRTL ? 'حمل' : 'Download'}
          </Button>
          <Button
            variant="outline"
            onClick={handleShare}
            className="flex-1"
          >
            <Share2 className="w-4 h-4 mr-2" />
            {isRTL ? 'شارك' : 'Share'}
          </Button>
        </div>

        {/* Start New Order Button */}
        <Button
          className="w-full mt-4 h-12 text-base"
          onClick={onStartNewOrder}
        >
          {isRTL ? 'بدء طلب جديد' : 'Start New Order'}
        </Button>
      </div>
    );
  }

  // Confirmation screen
  return (
    <div className="w-full max-w-md mx-auto text-center px-4 py-8 min-h-screen flex flex-col justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Success Icon - Pulse animation */}
      <div className="mb-6 flex justify-center">
        <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-4 animate-in zoom-in duration-500">
          <CheckCircle2 className="h-16 w-16 text-green-600 dark:text-green-400 animate-pulse" />
        </div>
      </div>

      {/* Success Message - Fade in */}
      <h1 className="text-3xl md:text-4xl font-bold mb-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150">
        {isRTL ? 'تم تأكيد الطلب!' : 'Order Confirmed!'}
      </h1>

      {/* Subheader - Thank you message */}
      <p className="text-muted-foreground mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
        {isRTL ? 'شكراً لطلبك' : 'Thank you for your order'}
      </p>

      {/* Order Number - Staggered fade in */}
      <div className="mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-450">
        <p className="text-muted-foreground mb-2 text-sm">
          {isRTL ? 'رقم الطلب' : 'Order Number'}
        </p>
        <p className="text-2xl md:text-3xl font-bold">#{shortOrderId}</p>
      </div>

      {/* ETA - Staggered fade in */}
      <div className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-600">
        <p className="text-lg md:text-xl">
          {isRTL ? `جاهز في ${eta} دقيقة` : `Ready in ${eta} minutes`}
        </p>
      </div>

      {/* Action Buttons - Staggered fade in */}
      <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-750">
        <Button
          className="w-full h-12 text-base md:h-14 md:text-lg"
          onClick={() => setShowReceipt(true)}
        >
          {isRTL ? 'عرض الإيصال' : 'View Receipt'}
        </Button>
        <Button
          variant="outline"
          className="w-full h-12 text-base md:h-14 md:text-lg"
          onClick={onStartNewOrder}
        >
          {isRTL ? 'بدء طلب جديد' : 'Start New Order'}
        </Button>
      </div>
    </div>
  );
}








